import { pipe } from 'fp-ts/function';
import { array, string } from 'fp-ts';
import { useEffect, useMemo, useState } from 'react';
import { LocalizedText, UseDialogActionReturn, useTimezone } from '@mews/framework';
import { PortfolioVoucherOverview } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { getCollectionIds } from '@mews/fp';
import { getWallClockDateUtcString } from '@mews-commander/utils';
import { prepareBulkEditValues, separateByUniformValues } from '../../../../utilities';
import { checkIfSomeEntitiesAreFromSameEnterprise } from '../../../../utils';
import { useRateIntersection, useEditVoucher } from '../../../../queries';
import { FormErrors } from '../../../../types';
import { FieldName, FieldsConfig, FormValues } from './types';
import { utcLocalizedDate } from './helpers';
import { VoucherContainer } from './VoucherContainer';

interface EditVoucherContainerProps extends UseDialogActionReturn {
    onSuccess: () => void;
    selectedVouchers: Array<PortfolioVoucherOverview>;
    selectedVouchersCount: number;
}

const defaultVoucherPropertiesToCheck: Array<Exclude<FieldName, 'rateKeys' | 'properties'>> = [
    'name',
    'key',
    'occupiableIntervalEndUtc',
    'occupiableIntervalStartUtc',
    'companyKey',
];

export const EditVoucherContainer = ({
    states,
    handlers,
    selectedVouchers,
    selectedVouchersCount,
    onSuccess,
}: EditVoucherContainerProps) => {
    const title = <LocalizedText textKey={selectedVouchersCount > 1 ? TextKey.VoucherBulkEdit : TextKey.EditVoucher} />;
    const editVoucher = useEditVoucher();
    const [formErrors, setFormErrors] = useState<FormErrors>();
    const [unlockedFields, setUnlockedFields] = useState<FieldName[]>([]);

    const unlockField = (fieldName: FieldName) => {
        setUnlockedFields((prev) => [...prev, fieldName]);
    };

    useEffect(() => {
        if (!states.isDialogOpened) {
            setFormErrors(undefined);
            setUnlockedFields([]);
        }
    }, [states.isDialogOpened]);

    const timezone = useTimezone();

    const voucherIds = getCollectionIds(selectedVouchers);
    const enterpriseIds = pipe(
        selectedVouchers,
        array.map((voucher) => voucher.enterpriseId),
        array.uniq(string.Eq)
    );

    const { data: intersectedRateKeysResult } = useRateIntersection({
        enterpriseIds,
        voucherIds,
        enabled: states.isDialogOpened,
    });

    const { initialFormValues, fieldsConfig } = useMemo(() => {
        if (array.isEmpty(selectedVouchers) || !states.isDialogOpened) {
            return {};
        }

        const isAssignedRatesFieldDisabled =
            intersectedRateKeysResult?.keys == null || array.isEmpty(intersectedRateKeysResult?.keys);
        const isAssignedRatesFieldLocked = intersectedRateKeysResult?.hasDisjointAssignedKeys;
        const assignedRatesInitialValue = intersectedRateKeysResult?.assignedKeys || [];

        const { left: initialLockedFields, right: initialEnabledFields } = separateByUniformValues(
            selectedVouchers,
            defaultVoucherPropertiesToCheck
        );

        const areSomeVouchersFromSameEnterprise = checkIfSomeEntitiesAreFromSameEnterprise(selectedVouchers);
        const voucherKeyDisabledField: FieldName[] = areSomeVouchersFromSameEnterprise ? ['key'] : [];
        const assignedRatesDisabledFields: FieldName[] = isAssignedRatesFieldDisabled ? ['rateKeys'] : [];
        const assignedRatesLockedFields: FieldName[] = isAssignedRatesFieldLocked ? ['rateKeys'] : [];
        const assignedRatesEnabledFields: FieldName[] = !isAssignedRatesFieldDisabled ? ['rateKeys'] : [];

        const config: FieldsConfig = {
            disabledFields: [...assignedRatesDisabledFields, ...voucherKeyDisabledField],
            enabledFields: [...initialEnabledFields, ...assignedRatesEnabledFields],
            lockedFields: [...initialLockedFields, ...assignedRatesLockedFields],
        };

        const { enabledFields } = config;

        const firstVoucher = selectedVouchers[0];
        const formValues: FormValues = {
            name: (enabledFields?.includes('name') && firstVoucher.name) || '',
            key: (enabledFields?.includes('key') && firstVoucher.key) || '',
            occupiableIntervalStartUtc: enabledFields?.includes('occupiableIntervalStartUtc')
                ? utcLocalizedDate(firstVoucher.occupiableIntervalStartUtc, timezone)
                : null,
            occupiableIntervalEndUtc: enabledFields?.includes('occupiableIntervalEndUtc')
                ? utcLocalizedDate(firstVoucher.occupiableIntervalEndUtc, timezone)
                : null,
            rateKeys: assignedRatesInitialValue,
            companyKey: (enabledFields?.includes('companyKey') && firstVoucher.companyKey) || '',
        };

        return { initialFormValues: formValues, fieldsConfig: config };
    }, [selectedVouchers, intersectedRateKeysResult, timezone, states.isDialogOpened]);

    const handleSubmit = ({ occupiableIntervalStartUtc, occupiableIntervalEndUtc, ...rest }: FormValues) => {
        const updatedValues = prepareBulkEditValues({
            values: {
                ...rest,
                occupiableIntervalStart: getWallClockDateUtcString(occupiableIntervalStartUtc) || null,
                occupiableIntervalEnd: getWallClockDateUtcString(occupiableIntervalEndUtc) || null,
            },
            initialFormValues: {
                ...initialFormValues,
                occupiableIntervalStart:
                    getWallClockDateUtcString(initialFormValues?.occupiableIntervalStartUtc) || null,
                occupiableIntervalEnd: getWallClockDateUtcString(initialFormValues?.occupiableIntervalEndUtc) || null,
            },
            unlockedFields,
        });
        editVoucher.mutate(
            {
                voucherIds,
                ...updatedValues,
            },
            {
                onSuccess,
                onError: (e) => {
                    if (e instanceof Error && e.message.includes('CompanyKey')) {
                        setFormErrors({ field: 'companyKey', message: e.message });
                    }

                    if (e instanceof Error && e.message.includes('voucher key')) {
                        setFormErrors({ field: 'key', message: e.message });
                    }
                },
            }
        );
    };

    return (
        <VoucherContainer
            formErrors={formErrors}
            fieldsConfig={fieldsConfig}
            values={initialFormValues}
            active={states.isDialogOpened}
            onCancel={handlers.handleCancel}
            title={title}
            unlockField={unlockField}
            unlockedFields={unlockedFields}
            voucherIds={voucherIds}
            enterpriseIds={enterpriseIds}
            submitButtonTextKey={TextKey.Save}
            selectedVouchersCount={selectedVouchersCount}
            onSubmit={handleSubmit}
            propertyCount={enterpriseIds.length}
            isLoading={editVoucher.isPending}
        />
    );
};
